import React, { useState, useEffect, useCallback } from 'react';
import {
	Button,
	Collapse,
	Input,
	InputNumber,
	Select,
	Tooltip,
	Form,
} from 'antd';
import getSymbolFromCurrency from 'currency-symbol-map';
import _, { get } from 'lodash';
import {
	camelCase,
	lowerCase,
	parse,
	strip,
	ml,
} from '../../_shared/services/utils.js';
import { CategoryItem } from './category-items/CategoryItemComponent.jsx';

const { Option } = Select;

const ReferralTiers = (props) => {
	const {
		currentUser,
		allMultiLingualData,
		tierKeyBase,
		currentTiers = [],
		userGroups = [],
		updateExclusions,
		updateInclusions,
		theme,
		form,
	} = props;

	// State initialization
	const [state, setState] = useState({
		tiers: [],
		exclusions: [],
		inclusions: [],
		userGroup: null,
	});

	// Helper to update state
	const updateState = useCallback((updates) => {
		setState((prevState) => ({ ...prevState, ...updates }));
	}, []);

	// Helper functions
	const getGroupCurrency = useCallback(
		(userGroup) => {
			const foundGroup = userGroups.find((group) => group.id === userGroup);
			return get(foundGroup, 'currency', '');
		},
		[userGroups]
	);

	const getGroupName = useCallback(
		(userGroup) => {
			const foundGroup = userGroups.find((group) => group.id === userGroup);
			return get(foundGroup, 'name', '');
		},
		[userGroups]
	);

	const getExclusions = useCallback(
		(group, allTiers) => {
			try {
				const currentTiers = allTiers ? allTiers : get(state, 'tiers', []);
				const tiers = currentTiers.map((tier) => JSON.parse(tier));
				const exclusions = tiers.find((tier) => tier.userGroup === group);
				const result = exclusions?.rules?.exclusions;
				return result ? result : [];
			} catch {
				return [];
			}
		},
		[state]
	);

	const getInclusions = useCallback(
		(group, allTiers) => {
			try {
				const currentTiers = allTiers ? allTiers : get(state, 'tiers', []);
				const tiers = currentTiers.map((tier) => JSON.parse(tier));
				const inclusions = tiers.find((tier) => tier.userGroup === group);
				const result = inclusions?.rules?.inclusions;
				return result ? result : [];
			} catch {
				return [];
			}
		},
		[state]
	);

	// Initialize component
	useEffect(() => {
		const defaultUserGroup = userGroups.filter(
			(group) => group.name === 'Default'
		);
		let userGroupId = null;
		if (defaultUserGroup && defaultUserGroup[0]) {
			userGroupId = defaultUserGroup[0].id;
		}

		let tier = { userGroup: userGroupId };
		tier = JSON.stringify(tier);
		const tiers = currentTiers.length === 0 ? [tier] : currentTiers;
		const exclusions = getExclusions(userGroupId, tiers);
		const inclusions = getInclusions(userGroupId, tiers);

		updateState({
			tiers,
			exclusions,
			inclusions,
			userGroup: userGroupId,
		});
	}, [currentTiers, userGroups]); // Removed getExclusions, getInclusions to prevent infinite loop

	// Event handlers
	const handleAddDiversityBonus = useCallback(
		(tier, index) => {
			const { tiers } = state;
			const newTier = {
				...tier,
				rules: { diversityHire: { active: true, amount: null } },
			};
			const newTiers = [...tiers];
			newTiers[index] = JSON.stringify(newTier);
			updateState({ tiers: newTiers });
		},
		[state]
	);

	const handleRemoveDiversityBonus = useCallback(
		(tier, index) => {
			const { tiers } = state;
			const newTier = { ...tier };
			delete newTier.rules.diversityHire;
			const newTiers = [...tiers];
			newTiers[index] = JSON.stringify(newTier);
			updateState({ tiers: newTiers });
		},
		[state]
	);

	const handleAddExclusionRule = useCallback(
		(group) => {
			const { exclusions = [] } = state;
			const rule = { userGroup: group };
			updateState({
				exclusions: [...exclusions, rule],
			});
		},
		[state]
	);

	const handleAddInclusionRule = useCallback(
		(group) => {
			const { inclusions = [] } = state;
			const rule = { userGroup: group };
			updateState({
				inclusions: [...inclusions, rule],
			});
		},
		[state]
	);

	const handleAddTier = useCallback(
		(group) => {
			const { tiers } = state;
			let tier = { userGroup: group };
			tier = JSON.stringify(tier);
			updateState({
				tiers: [...tiers, tier],
			});
		},
		[state]
	);

	const handleRemoveExclusionRuleClick = useCallback(
		(itemIndex) => {
			const { exclusions = [] } = state;
			const copy = [...exclusions];
			copy.splice(itemIndex, 1);
			updateState({ exclusions: copy });
		},
		[state]
	);

	const handleRemoveInclusionRuleClick = useCallback(
		(itemIndex) => {
			const { inclusions = [] } = state;
			const copy = [...inclusions];
			copy.splice(itemIndex, 1);
			updateState({ inclusions: copy });
		},
		[state]
	);

	const handleRemoveTierClick = useCallback(
		(itemIndex) => {
			const { tiers } = state;
			const copy = [...tiers];
			copy.splice(itemIndex, 1);
			updateState({ tiers: copy });
		},
		[state]
	);

	// Render methods
	const renderExcludePicker = useCallback(
		(group, index, key, value, tierKeyBase) => {
			const { exclusions, tiers } = state;
			let categories = value || [];
			const stateKey = `${camelCase(key)}Categories`;
			const callback = (value) => updateState({ [stateKey]: value });

			const handleEnterPress = (e) => {
				const { value } = e.target;
				if (!value) return;

				categories.push(value);
				const exclusion = exclusions.find(
					(ex) => get(ex, 'type') === key && get(ex, 'userGroup') === group
				);
				const exclusionIndex = exclusions.findIndex(
					(ex) => get(ex, 'type') === key && get(ex, 'userGroup') === group
				);
				const newExclusions = [...exclusions];

				if (exclusion) {
					exclusion.value = categories;
					newExclusions[exclusionIndex] = exclusion;
					const updatedTiers = tiers.map((tier) => {
						const newTier = parse(tier);
						if (newTier.userGroup === group) {
							if (get(newTier, 'rules')) {
								newTier.rules.exclusions = newExclusions;
							} else {
								newTier.rules = { exclusions: newExclusions };
							}
						}
						return JSON.stringify(newTier);
					});
					if (updatedTiers) updateState({ tiers: updatedTiers });

					// Update form field with new exclusions
					const tierIndex = tiers.findIndex((t) => {
						const parsedTier = parse(t);
						return parsedTier.userGroup === group;
					});
					if (tierIndex >= 0) {
						form.setFieldsValue({
							[`tier-${group}-${tierIndex}-exclusions`]: newExclusions,
						});
					}
				}

				updateState({
					[stateKey]: categories,
					exclusions: newExclusions,
				});

				updateExclusions(newExclusions, tierKeyBase);

				// Clear the input field
				form.setFieldsValue({
					[`tier-${group}-${index}-${strip(lowerCase(key))}-excludepicker`]: '',
				});
				form.setFieldsValue({
					[`tier-${group}-${index}-${strip(lowerCase(key))}-excludepickervalue`]:
						newExclusions,
				});
			};

			return (
				<div
					key={`tier-${group}-${index}-${strip(lowerCase(key))}-excludepicker`}
					className="bs-form-grp"
				>
					<div className="custom-form-group">
						<label className="bs-label">Create {key}: </label>
						<Form.Item
							name={`tier-${group}-${index}-${strip(lowerCase(key))}-excludepicker`}
							initialValue=""
						>
							<Input
								className="custom-input"
								size="small"
								placeholder={`Enter ${key}`}
								onPressEnter={handleEnterPress}
							/>
						</Form.Item>
					</div>
					<div style={{ marginLeft: 20 }}>
						<CategoryItem
							categories={categories}
							callback={callback}
							exclusions={exclusions}
						/>
					</div>
				</div>
			);
		},
		[state, form, updateExclusions]
	);

	const renderIncludePicker = useCallback(
		(group, index, key, value, tierKeyBase) => {
			const { inclusions, tiers } = state;
			let categories = value || [];
			const stateKey = `${camelCase(key)}Categories`;
			const callback = (value) => updateState({ [stateKey]: value });

			const handleEnterPress = (e) => {
				const { value } = e.target;
				if (!value) return;

				categories.push(value);
				const inclusion = inclusions.find(
					(ex) => get(ex, 'type') === key && get(ex, 'userGroup') === group
				);
				const inclusionIndex = inclusions.findIndex(
					(ex) => get(ex, 'type') === key && get(ex, 'userGroup') === group
				);
				const newInclusions = [...inclusions];

				if (inclusion) {
					inclusion.value = categories;
					newInclusions[inclusionIndex] = inclusion;
					const updatedTiers = tiers.map((tier) => {
						const newTier = parse(tier);
						if (newTier.userGroup === group) {
							if (get(newTier, 'rules')) {
								newTier.rules.inclusions = newInclusions;
							} else {
								newTier.rules = { inclusions: newInclusions };
							}
						}
						return JSON.stringify(newTier);
					});
					if (updatedTiers) updateState({ tiers: updatedTiers });

					// Update form field with new inclusions
					const tierIndex = tiers.findIndex((t) => {
						const parsedTier = parse(t);
						return parsedTier.userGroup === group;
					});
					if (tierIndex >= 0) {
						form.setFieldsValue({
							[`tier-${group}-${tierIndex}-inclusions`]: newInclusions,
						});
					}
				}

				updateState({
					[stateKey]: categories,
					inclusions: newInclusions,
				});

				updateInclusions(newInclusions, tierKeyBase);

				// Clear the input field
				form.setFieldsValue({
					[`tier-${group}-${index}-${strip(lowerCase(key))}-includepicker`]: '',
				});
				form.setFieldsValue({
					[`tier-${group}-${index}-${strip(lowerCase(key))}-includepickervalue`]:
						newInclusions,
				});
			};

			return (
				<div
					key={`tier-${group}-${index}-${strip(lowerCase(key))}-includepicker`}
					className="bs-form-grp"
				>
					<div className="custom-form-group">
						<label className="bs-label">Create {key}: </label>
						<Form.Item
							name={`tier-${group}-${index}-${strip(lowerCase(key))}-includepicker`}
							initialValue=""
						>
							<Input
								className="custom-input"
								size="small"
								placeholder={`Enter ${key}`}
								onPressEnter={handleEnterPress}
							/>
						</Form.Item>
					</div>
					<div style={{ marginLeft: 20 }}>
						<CategoryItem
							categories={categories}
							callback={callback}
							exclusions={inclusions}
						/>
					</div>
				</div>
			);
		},
		[state, form, updateInclusions]
	);

	const renderExcludeCategory = useCallback(
		(group, index, tierKeyBase) => {
			const { exclusions = [] } = state;
			const rule = exclusions[index];
			const category = rule?.type;
			const { value } = rule || {};

			const categoryTypes = [
				'Department',
				'Role',
				'Job Classification',
				'Job Profile',
				'Management Level',
				'Job Family',
				'Employee Type',
			];

			if (categoryTypes.includes(category)) {
				return renderExcludePicker(group, index, category, value, tierKeyBase);
			}

			return null;
		},
		[state, renderExcludePicker]
	);

	const renderIncludeCategory = useCallback(
		(group, index, tierKeyBase) => {
			const { inclusions = [] } = state;
			const rule = inclusions[index];
			const category = rule?.type;
			const { value } = rule || {};

			const categoryTypes = [
				'Department',
				'Role',
				'Job Classification',
				'Job Profile',
				'Management Level',
				'Job Family',
				'Employee Type',
			];

			if (categoryTypes.includes(category)) {
				return renderIncludePicker(group, index, category, value, tierKeyBase);
			}

			return null;
		},
		[state, renderIncludePicker]
	);

	const renderExclusions = useCallback(
		(group, index) => {
			const { exclusions = [] } = state;

			const categoryOptions = [
				'Department',
				'Employee Type',
				'Job Classification',
				'Job Family',
				'Job Profile',
				'Management Level',
				'Role',
			];

			const options = categoryOptions
				.filter(
					(category) =>
						!exclusions.find(
							(exc) => exc.type === category && exc.userGroup === group
						)
				)
				.map((category) => (
					<Select.Option key={category} value={category}>
						{category}
					</Select.Option>
				));

			return exclusions.map((rule, ruleIndex) => {
				if (typeof rule === 'string') {
					rule = parse(rule);
				}

				const disabled = get(rule, 'value', []).length > 0;
				return (
					rule.userGroup === group && (
						<Collapse
							key={'exclusion' + group + ruleIndex}
							defaultActiveKey={['0']}
							expandIconPosition="start"
							collapsible="header"
							bordered={false}
							className="bs-form-collapse"
						>
							<Collapse.Panel
								key="0"
								className="bs-form-collapse-card"
								collapsible="disabled"
								header={
									<div className="bs-form-grp">
										<i className="icon-arrow-down bs-form-collapse-icon" />
										<div className="custom-form-group">
											<label className="bs-label">Exclude employees by</label>
											<Form.Item
												name={`tier-${tierKeyBase}-${ruleIndex}-${strip(lowerCase(rule.type))}-exclusionscategory`}
												initialValue={rule.type || null}
											>
												<Select
													placeholder="Category"
													className="custom-input"
													style={{ width: 180 }}
													disabled={disabled}
													onSelect={(value) => {
														const newRule = { ...rule, type: value };
														const newExclusions = [...exclusions];
														newExclusions[ruleIndex] = newRule;
														updateState({ exclusions: newExclusions });
														updateExclusions(newExclusions, tierKeyBase);

														// Update form field with new exclusions
														const { tiers: currentTiers } = state;
														const tierIndex = currentTiers.findIndex((t) => {
															const parsedTier = parse(t);
															return parsedTier.userGroup === group;
														});
														if (tierIndex >= 0) {
															form.setFieldsValue({
																[`tier-${group}-${tierIndex}-exclusions`]:
																	newExclusions,
															});
														}
													}}
												>
													{options}
												</Select>
											</Form.Item>
											<Form.Item
												name={`tier-${tierKeyBase}-${ruleIndex}-exclusions`}
												initialValue={exclusions}
											>
												<Input type="hidden" className="custom-input" />
											</Form.Item>
										</div>
										<Button
											type="link"
											className="btn-link danger"
											onClick={() => handleRemoveExclusionRuleClick(ruleIndex)}
										>
											<i className="icon-bin" />
											Delete this rule
										</Button>
									</div>
								}
							>
								{renderExcludeCategory(group, ruleIndex, tierKeyBase)}
							</Collapse.Panel>
						</Collapse>
					)
				);
			});
		},
		[
			state,
			tierKeyBase,
			handleRemoveExclusionRuleClick,
			updateExclusions,
			renderExcludeCategory,
		]
	);

	const renderInclusions = useCallback(
		(group, index) => {
			const { inclusions = [] } = state;

			const categoryOptions = [
				'Department',
				'Employee Type',
				'Job Classification',
				'Job Family',
				'Job Profile',
				'Management Level',
				'Role',
			];

			const options = categoryOptions
				.filter(
					(category) =>
						!inclusions.find(
							(inc) => inc.type === category && inc.userGroup === group
						)
				)
				.map((category) => (
					<Select.Option key={category} value={category}>
						{category}
					</Select.Option>
				));

			return inclusions.map((rule, ruleIndex) => {
				if (typeof rule === 'string') {
					rule = parse(rule);
				}

				const disabled = get(rule, 'value', []).length > 0;
				return (
					rule.userGroup === group && (
						<Collapse
							key={'inclusion' + group + ruleIndex}
							defaultActiveKey={['0']}
							collapsible="header"
							bordered={false}
							expandIconPosition="start"
							className="bs-form-collapse"
						>
							<Collapse.Panel
								key="0"
								className="bs-form-collapse-card"
								collapsible="disabled"
								header={
									<div key={ruleIndex} className="bs-form-grp">
										<i className="icon-arrow-down bs-form-collapse-icon" />
										<div className="custom-form-group">
											<label className="bs-label">Include employees by</label>
											<Form.Item
												name={`tier-${tierKeyBase}-${strip(lowerCase(rule.type))}-${ruleIndex}-inclusionscategory`}
												initialValue={rule.type || null}
											>
												<Select
													placeholder="Category"
													className="custom-input"
													style={{ width: 180 }}
													disabled={disabled}
													onSelect={(value) => {
														const newRule = { ...rule, type: value };
														const newInclusions = [...inclusions];
														newInclusions[ruleIndex] = newRule;
														updateState({ inclusions: newInclusions });
														updateInclusions(newInclusions, tierKeyBase);

														// Update form field with new inclusions
														const { tiers: currentTiers } = state;
														const tierIndex = currentTiers.findIndex((t) => {
															const parsedTier = parse(t);
															return parsedTier.userGroup === group;
														});
														if (tierIndex >= 0) {
															form.setFieldsValue({
																[`tier-${group}-${tierIndex}-inclusions`]:
																	newInclusions,
															});
														}
													}}
												>
													{options}
												</Select>
											</Form.Item>
											<Form.Item
												name={`tier-${tierKeyBase}-${ruleIndex}-inclusions`}
												initialValue={inclusions}
											>
												<Input hidden type="text" className="custom-input" />
											</Form.Item>
										</div>
										<Button
											type="link"
											className="btn-link danger"
											onClick={() => handleRemoveInclusionRuleClick(ruleIndex)}
										>
											<i className="icon-bin" />
											Delete this rule
										</Button>
									</div>
								}
							>
								{renderIncludeCategory(group, ruleIndex, tierKeyBase)}
							</Collapse.Panel>
						</Collapse>
					)
				);
			});
		},
		[
			state,
			tierKeyBase,
			handleRemoveInclusionRuleClick,
			updateInclusions,
			renderIncludeCategory,
		]
	);

	const renderTierInputs = useCallback(
		(group) => {
			const { tiers = [] } = state;
			const currencyCode = getGroupCurrency(group);

			return tiers.map((tier, index) => {
				if (typeof tier === 'string') {
					tier = parse(tier);
				}

				return (
					tier.userGroup === group && (
						<div key={index} className="bs-form-grp">
							<div className="custom-form-group">
								<label className="bs-label">
									{getSymbolFromCurrency(currencyCode)}
								</label>
								<Form.Item
									name={`tier-${tierKeyBase}-${index}-usergroup`}
									initialValue={group}
								>
									<Input
										hidden
										className="custom-input"
										placeholder={ml(
											'Bonus Amount',
											currentUser,
											allMultiLingualData
										)}
									/>
								</Form.Item>
								<Form.Item
									name={`tier-${tierKeyBase}-${index}`}
									initialValue={tier.amount}
									rules={[{ required: true, message: 'Enter a bonus amount.' }]}
								>
									<InputNumber
										min={0}
										className="custom-input"
										placeholder={ml(
											'Bonus Amount',
											currentUser,
											allMultiLingualData
										)}
									/>
								</Form.Item>
							</div>
							<div className="custom-form-group">
								<label className="bs-label">
									{ml('after', currentUser, allMultiLingualData)}
								</label>
								<div className="cf-addon-wrap">
									<Form.Item
										name={`tier-${tierKeyBase}-${index}-payoutdays`}
										initialValue={tier.payOutDays}
										rules={[{ required: true, message: 'Enter pay out days.' }]}
									>
										<InputNumber
											min={0}
											className="custom-input"
											placeholder={ml(
												'Days from hire date',
												currentUser,
												allMultiLingualData
											)}
										/>
									</Form.Item>
									<div className="cf-addon">days</div>
								</div>
							</div>
							<div className="custom-form-group">
								<label className="bs-label">payable to</label>
								<Form.Item
									name={`tier-${tierKeyBase}-${index}-recipienttype`}
									initialValue={tier.recipientType || 'employee'}
								>
									<Select placeholder="Recipient Type" className="custom-input">
										<Select.Option value="candidate">
											{ml('Candidate', currentUser, allMultiLingualData)}
										</Select.Option>
										<Select.Option value="employee">
											{ml('Employee', currentUser, allMultiLingualData)}
										</Select.Option>
									</Select>
								</Form.Item>
							</div>
							{!get(tier, 'rules.diversityHire.active') && (
								<Button
									type="link"
									className="add-btn"
									size="small"
									onClick={() => handleAddDiversityBonus(tier, index)}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									Add Diversity Incentive
								</Button>
							)}
							{get(tier, 'rules.diversityHire.active') && (
								<div className="custom-form-group">
									<Tooltip title="Cancel Diversity Incentive.">
										<Button
											type="link"
											className="btn-link danger"
											onClick={() => handleRemoveDiversityBonus(tier, index)}
										>
											<i className="icon-bin" />
										</Button>
									</Tooltip>
									<label className="bs-label">
										{`Diversity Hire Amount:`}{' '}
										{getSymbolFromCurrency(currencyCode)}
									</label>
									<Form.Item
										name={`tier-${tierKeyBase}-${index}-diversityincentive`}
										initialValue={get(tier, 'rules.diversityHire.amount')}
										rules={[
											{ required: true, message: 'Enter a bonus amount.' },
										]}
									>
										<InputNumber
											min={0}
											className="custom-input"
											placeholder="Amount"
										/>
									</Form.Item>
								</div>
							)}

							{index > 0 && (
								<Tooltip title="Delete this tier.">
									<Button
										type="link"
										className="btn-link danger"
										onClick={() => handleRemoveTierClick(index)}
									>
										<i className="icon-bin" />
									</Button>
								</Tooltip>
							)}
						</div>
					)
				);
			});
		},
		[
			state,
			tierKeyBase,
			getGroupCurrency,
			currentUser,
			allMultiLingualData,
			handleAddDiversityBonus,
			handleRemoveDiversityBonus,
			handleRemoveTierClick,
		]
	);

	const renderUserGroup = useCallback(
		(group, index) => {
			return (
				<div key={'renderug' + group + index}>
					<h6 className="bs-form-label">
						{getGroupName(group)}{' '}
						{ml('Group', currentUser, allMultiLingualData)}:
					</h6>
					<div className="bs-form-grp">
						<div className="custom-form-group">
							<label className="bs-label">Exclusions:</label>
							<Button
								type="link"
								className="add-btn"
								size="small"
								onClick={() => handleAddExclusionRule(group)}
							>
								<span className="icon-circle">
									<i className="icon-plus" />
								</span>
								Add Exclusion Rule
							</Button>
						</div>
					</div>
					{renderExclusions(group, index)}
					<div className="bs-form-grp">
						<div className="custom-form-group">
							<label className="bs-label">Inclusions:</label>
							<Button
								type="link"
								className="add-btn"
								size="small"
								onClick={() => handleAddInclusionRule(group)}
							>
								<span className="icon-circle">
									<i className="icon-plus" />
								</span>
								Add Inclusion Rule
							</Button>
						</div>
					</div>
					{renderInclusions(group, index)}
					<h6 className="bs-form-label">Payments:</h6>
					{renderTierInputs(group)}
					<Button
						type="primary"
						size="large"
						onClick={() => handleAddTier(group)}
					>
						{ml('Add Bonus Tier', currentUser, allMultiLingualData)}
					</Button>
				</div>
			);
		},
		[
			getGroupName,
			currentUser,
			allMultiLingualData,
			handleAddExclusionRule,
			renderExclusions,
			handleAddInclusionRule,
			renderInclusions,
			renderTierInputs,
			handleAddTier,
		]
	);

	const renderUserGroups = useCallback(() => {
		const { tiers } = state;
		let groups = [];
		for (const tier of tiers) groups.push(get(parse(tier), 'userGroup'));
		groups = _.uniq(groups);
		const allUserGroups = [];
		for (const [index, group] of groups.entries()) {
			allUserGroups.push(renderUserGroup(group, index));
		}
		return allUserGroups;
	}, [state, renderUserGroup]);

	// Main render
	const { userGroup } = state;
	const options = _.sortBy(userGroups, ['name']).map((group, index) => (
		<Select.Option key={'usrG' + index} value={group.id}>
			{group.name}
		</Select.Option>
	));

	return (
		<div>
			<h6 className="bs-form-label">
				{ml('Bonus Payments', currentUser, allMultiLingualData)}
			</h6>
			<div className="bs-form-grp">
				<div className="custom-form-group">
					<label className="bs-label">
						{ml('Choose An Employee Group', currentUser, allMultiLingualData)}:
					</label>
					<Select
						defaultValue={userGroup}
						value={userGroup}
						className="custom-input"
						onChange={(selectedUserGroup) =>
							updateState({ userGroup: selectedUserGroup })
						}
					>
						{options}
					</Select>
				</div>
				<Button
					type="link"
					className="add-btn"
					size="small"
					onClick={() => handleAddTier(userGroup)}
				>
					<span className="icon-circle">
						<i className="icon-plus" />
					</span>
					{ml('Add Group Bonus', currentUser, allMultiLingualData)}
				</Button>
			</div>
			{renderUserGroups()}
		</div>
	);
};

export default ReferralTiers;
